import React from 'react'
import { X, Building2, CreditCard, Settings } from 'lucide-react'
import { CreateCompanyDto } from '@/types/company'
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import { Check, ChevronsUpDown } from "lucide-react"
import { cn } from "@/lib/utils"
import './AddCompanyModal.css'

interface AddCompanyModalProps {
  onClose: () => void
  onSubmit: (data: CreateCompanyDto) => Promise<void>
}

// Türkiye illeri listesi
const TURKISH_CITIES = [
  { value: "adana", label: "Adana" },
  { value: "adiyaman", label: "Adıyaman" },
  { value: "afyonkarahisar", label: "Afyonkarahisar" },
  { value: "agri", label: "Ağrı" },
  { value: "amasya", label: "Amasya" },
  { value: "ankara", label: "Ankara" },
  { value: "antalya", label: "Antalya" },
  { value: "artvin", label: "Artvin" },
  { value: "aydin", label: "Aydın" },
  { value: "balikesir", label: "Balıkesir" },
  { value: "bilecik", label: "Bilecik" },
  { value: "bingol", label: "Bingöl" },
  { value: "bitlis", label: "Bitlis" },
  { value: "bolu", label: "Bolu" },
  { value: "burdur", label: "Burdur" },
  { value: "bursa", label: "Bursa" },
  { value: "canakkale", label: "Çanakkale" },
  { value: "cankiri", label: "Çankırı" },
  { value: "corum", label: "Çorum" },
  { value: "denizli", label: "Denizli" },
  { value: "diyarbakir", label: "Diyarbakır" },
  { value: "edirne", label: "Edirne" },
  { value: "elazig", label: "Elazığ" },
  { value: "erzincan", label: "Erzincan" },
  { value: "erzurum", label: "Erzurum" },
  { value: "eskisehir", label: "Eskişehir" },
  { value: "gaziantep", label: "Gaziantep" },
  { value: "giresun", label: "Giresun" },
  { value: "gumushane", label: "Gümüşhane" },
  { value: "hakkari", label: "Hakkari" },
  { value: "hatay", label: "Hatay" },
  { value: "isparta", label: "Isparta" },
  { value: "mersin", label: "Mersin" },
  { value: "istanbul", label: "İstanbul" },
  { value: "izmir", label: "İzmir" },
  { value: "kars", label: "Kars" },
  { value: "kastamonu", label: "Kastamonu" },
  { value: "kayseri", label: "Kayseri" },
  { value: "kirklareli", label: "Kırklareli" },
  { value: "kirsehir", label: "Kırşehir" },
  { value: "kocaeli", label: "Kocaeli" },
  { value: "konya", label: "Konya" },
  { value: "kutahya", label: "Kütahya" },
  { value: "malatya", label: "Malatya" },
  { value: "manisa", label: "Manisa" },
  { value: "kahramanmaras", label: "Kahramanmaraş" },
  { value: "mardin", label: "Mardin" },
  { value: "mugla", label: "Muğla" },
  { value: "mus", label: "Muş" },
  { value: "nevsehir", label: "Nevşehir" },
  { value: "nigde", label: "Niğde" },
  { value: "ordu", label: "Ordu" },
  { value: "rize", label: "Rize" },
  { value: "sakarya", label: "Sakarya" },
  { value: "samsun", label: "Samsun" },
  { value: "siirt", label: "Siirt" },
  { value: "sinop", label: "Sinop" },
  { value: "sivas", label: "Sivas" },
  { value: "tekirdag", label: "Tekirdağ" },
  { value: "tokat", label: "Tokat" },
  { value: "trabzon", label: "Trabzon" },
  { value: "tunceli", label: "Tunceli" },
  { value: "sanliurfa", label: "Şanlıurfa" },
  { value: "usak", label: "Uşak" },
  { value: "van", label: "Van" },
  { value: "yozgat", label: "Yozgat" },
  { value: "zonguldak", label: "Zonguldak" },
  { value: "aksaray", label: "Aksaray" },
  { value: "bayburt", label: "Bayburt" },
  { value: "karaman", label: "Karaman" },
  { value: "kirikkale", label: "Kırıkkale" },
  { value: "batman", label: "Batman" },
  { value: "sirnak", label: "Şırnak" },
  { value: "bartin", label: "Bartın" },
  { value: "ardahan", label: "Ardahan" },
  { value: "igdir", label: "Iğdır" },
  { value: "yalova", label: "Yalova" },
  { value: "karabuk", label: "Karabük" },
  { value: "kilis", label: "Kilis" },
  { value: "osmaniye", label: "Osmaniye" },
  { value: "duzce", label: "Düzce" }
];

export const AddCompanyModal: React.FC<AddCompanyModalProps> = ({
  onClose,
  onSubmit
}) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [cityOpen, setCityOpen] = React.useState(false)
  const [formData, setFormData] = React.useState<CreateCompanyDto>({
    name: '',
    city: '',
    sector: '',
    phone: '',
    license: {
      type: 'trial',
      count: 1,
      expiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
    },
    ezanSettings: {
      pauseDuringPrayer: false
    }
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    if (name.startsWith('license.')) {
      const licenseField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        license: {
          ...prev.license,
          [licenseField]: licenseField === 'expiryDate' ? new Date(value) : value
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const handleSwitchChange = (checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      ezanSettings: {
        ...prev.ezanSettings,
        pauseDuringPrayer: checked
      }
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    try {
      await onSubmit(formData)
      onClose()
    } catch (error) {
      console.error('Firma eklenirken hata:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50">
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-card rounded-xl shadow-lg border border-border">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold">Yeni Firma Ekle</h2>
            <p className="text-sm text-muted-foreground">Firma bilgilerini ve lisans ayarlarını girin</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="basic" className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Temel Bilgiler
              </TabsTrigger>
              <TabsTrigger value="license" className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Lisans
              </TabsTrigger>
              <TabsTrigger value="settings" className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Ayarlar
              </TabsTrigger>
            </TabsList>

            {/* TAB 1: Temel Bilgiler */}
            <TabsContent value="basic" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Firma Adı *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Örn: Kahve Dükkanı"
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Telefon *</label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="5xx xxx xxxx"
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">İl *</label>
                  <Popover open={cityOpen} onOpenChange={setCityOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={cityOpen}
                        className="w-full justify-between bg-background"
                      >
                        {formData.city
                          ? TURKISH_CITIES.find((city) => city.value === formData.city)?.label
                          : "İl seçin..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0">
                      <Command>
                        <CommandInput placeholder="İl ara..." />
                        <CommandEmpty>İl bulunamadı.</CommandEmpty>
                        <CommandGroup className="max-h-64 overflow-auto">
                          {TURKISH_CITIES.map((city) => (
                            <CommandItem
                              key={city.value}
                              value={city.label}
                              onSelect={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  city: city.value
                                }))
                                setCityOpen(false)
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  formData.city === city.value ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {city.label}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Sektör *</label>
                  <select
                    name="sector"
                    value={formData.sector}
                    onChange={handleChange}
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
                    required
                  >
                    <option value="">Sektör Seçin</option>
                    <option value="restaurant">Restoran / Cafe</option>
                    <option value="retail">Perakende Mağazalar</option>
                    <option value="market">Market / Gıda</option>
                    <option value="hotel">Otel / Konaklama</option>
                    <option value="fitness">Spor Salonu / Fitness</option>
                    <option value="beauty">Güzellik / Kuaför</option>
                    <option value="healthcare">Sağlık / Medikal</option>
                    <option value="entertainment">Eğlence Merkezi</option>
                    <option value="shopping_mall">AVM</option>
                    <option value="office">Ofis / İş Merkezi</option>
                    <option value="education">Eğitim Kurumu</option>
                  </select>
                </div>
              </div>
            </TabsContent>

            {/* TAB 2: Lisans */}
            <TabsContent value="license" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Lisans Türü *</label>
                  <select
                    name="license.type"
                    value={formData.license.type}
                    onChange={handleChange}
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
                    required
                  >
                    <option value="trial">Trial (Deneme)</option>
                    <option value="premium">Premium (Ücretli)</option>
                  </select>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {formData.license.type === 'trial'
                      ? 'Deneme süresi için ücretsiz lisans'
                      : 'Tam özellikli ücretli lisans'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Lisans Adedi *</label>
                  <input
                    type="number"
                    name="license.count"
                    value={formData.license.count}
                    onChange={handleChange}
                    min="1"
                    placeholder="Kaç cihaz?"
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
                    required
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Kullanılabilir cihaz sayısı
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Bitiş Tarihi *
                </label>
                <input
                  type="date"
                  name="license.expiryDate"
                  value={formData.license.expiryDate.toISOString().split('T')[0]}
                  onChange={handleChange}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
                  required
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Lisansın geçerlilik süresi
                </p>
              </div>
            </TabsContent>

            {/* TAB 3: Ayarlar */}
            <TabsContent value="settings" className="space-y-4">
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h4 className="font-medium text-sm">Ezan Vakti Ayarı</h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      Ezan vakti geldiğinde müzik otomatik olarak duraklatılsın mı?
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.ezanSettings.pauseDuringPrayer}
                      onChange={(e) => handleSwitchChange(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end gap-2 mt-6 pt-6 border-t border-border">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              disabled={isSubmitting}
            >
              İptal
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Ekleniyor...' : 'Firma Ekle'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
